import React, { useState } from 'react';
import { useAuth } from '../../Context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../../Api/ApiUrlConst';
import {
  Container,
  Card,
  CardBody,
  Button,
} from 'reactstrap';
import {
  TextField,
  Typography,
  Box,
  Fade,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Domain as DomainIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { toast } from 'react-toastify';

const theme = createTheme({
  palette: {
    primary: { main: '#2563eb' },
    error: { main: '#dc3545' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, color: '#1e293b' },
    body2: { color: '#6b7280' },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#f9fafb',
            '&:hover fieldset': { borderColor: '#2563eb' },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 0',
          fontSize: '16px',
        },
      },
    },
  },
});

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // ✅ FIXED: Now handleLogin accepts FormEvent
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const { token, role, userID, department, name } = await loginApi(email, password);

      login(token, role, userID, department, name);

      if (role === 'Admin') navigate('/Admin');
      else if (role === 'ProjectManager') navigate('/Project');
      else if (role === 'TeamLeader') navigate('/TeamLead');
      else if (role === 'Client') navigate('/client');
      else navigate('/Member');

    } catch (err: any) {
      toast.warning(err.response?.message || 'Login Failed');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        fluid
        className="d-flex align-items-center justify-content-center justify-items-center"
        style={{
          minHeight: '91vh',
          margin: 'auto',
          backgroundColor: '#f1f5f9',
          backgroundImage: 'linear-gradient(to right, #e0f2fe, #f1f5f9)',
        }}
      >
        <Fade in timeout={800}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            maxWidth="400px"
            px={{ xs: 2, sm: 0 }}
            sx={{
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-3px)' },
            }}
          >
            <DomainIcon
              sx={{
                fontSize: 60,
                backgroundColor: '#2563eb',
                borderRadius: '50%',
                padding: 1.5,
                color: 'white',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                mb: 2,
              }}
            />

            <Typography variant="h4" align="center">
              Welcome to <span style={{ color: '#2563eb' }}>WoCons</span>
            </Typography>
            <Typography variant="body2" align="center" mb={3}>
              Task Management Portal
            </Typography>

            {/* ✅ FIXED FORM FOR ENTER KEY LOGIN */}
            <form onSubmit={handleLogin}>

              <Card
                style={{
        width: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                  border: 'none',
                  backgroundColor: '#ffffff',
        marginBottom: '250px',
                }}
              >
                <CardBody className="p-4">
                  <Typography variant="h4" align="center" mb={3}>
                    Login
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Box mb={2} width="100%">
                    <TextField
                      label="Email"
                      placeholder="abc@domain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputLabelProps={{ sx: { fontWeight: 500, color: '#1f2937' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#6b7280' }} />
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      type="email"
                      required
                      fullWidth
                    />
                  </Box>

                  <Box mb={3} width="100%">
                    <TextField
                      label="Password"
                      placeholder="**********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputLabelProps={{ sx: { fontWeight: 500, color: '#1f2937' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#6b7280' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                              {showPassword ? (
                                <VisibilityOff sx={{ color: '#6b7280' }} />
                              ) : (
                                <Visibility sx={{ color: '#6b7280' }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      type={showPassword ? 'text' : 'password'}
                      required
                      fullWidth
                    />
                  </Box>

                  <div className="text-center">
                    <Button
                      color="primary"
                      type="submit"
                      sx={{
                        background: 'linear-gradient(to right, #2563eb, #1d4ed8)',
                        color: 'white',
                        fontWeight: 600,
                        boxShadow: '0 4px 10px rgba(37, 99, 235, 0.2)',
                        transition: '0.3s',
                        '&:hover': {
                          background: 'linear-gradient(to right, #1e40af, #1e3a8a)',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </form>
          </Box>
        </Fade>
      </Container>
    </ThemeProvider>
  );
};

export default LoginPage;
